import { Box, Chip, Stack, Tooltip, Typography } from '@mui/material';
import {
  snapshotAccounts,
  venueAccountKey,
  type OpenerDescalceAccountMeta,
  type OpenerDescalceSnapshot
} from '../../api/useOpenerDescalce';
import type { DescalceTotals } from './descalceFilters';
import { TriageIndexNotice } from '../triage/TriageIndexNotice';
import type { UseTriageIndexResult } from '../../api/useTriageIndex';

const PLATFORM_LABEL: Record<OpenerDescalceAccountMeta['platform'], string> = {
  kalshi: 'Kalshi',
  polymarket: 'Poly'
};

const SOURCE_COLOR: Record<
  OpenerDescalceAccountMeta['source'],
  'success' | 'warning' | 'error' | 'default'
> = {
  venue: 'success',
  starting: 'warning',
  unconfigured: 'default',
  error: 'error'
};

/**
 * One chip per venue-account the snapshot carries — never a fixed pair. The
 * fleet is data: a build that renders a hardcoded number of chips silently
 * drops every account past it, and an account whose read is failing would then
 * be invisible on the one screen meant to catch it.
 */
const AccountChip = ({ account }: { account: OpenerDescalceAccountMeta }) => {
  const lastSuccess =
    account.lastSuccessAt != null
      ? new Date(account.lastSuccessAt).toLocaleTimeString()
      : 'never';
  const title =
    `${PLATFORM_LABEL[account.platform]} account "${account.accountId}" · source: ${account.source} · last success: ${lastSuccess}` +
    (account.redeemableExcluded
      ? ` · ${account.redeemableExcluded} redeemable row(s) excluded from counts`
      : '') +
    (account.lastError ? ` · ${account.lastError}` : '');
  return (
    <Tooltip title={title}>
      <Chip
        size="small"
        label={`${PLATFORM_LABEL[account.platform]} ${account.accountId}: ${account.source}`}
        color={SOURCE_COLOR[account.source] ?? 'default'}
        variant="outlined"
        data-account={venueAccountKey(account.platform, account.accountId)}
        data-account-source={account.source}
        sx={{ fontSize: '0.65rem', height: 20 }}
      />
    </Tooltip>
  );
};

/**
 * Header strip: visible-row count, total descalce (contracts, NON-ambiguous
 * rows only — labelled as such), ambiguous/resolving/unread counts, the rules
 * the sweeper could not resolve an account pair for, and one freshness/source
 * chip per VENUE-ACCOUNT in the snapshot.
 */
export const DescalceHeaderStrip = ({
  totals,
  snapshot,
  resolvingHidden,
  triage
}: {
  totals: DescalceTotals;
  snapshot: OpenerDescalceSnapshot | undefined;
  /** Rows the `hide settled` filter is suppressing. Reported so the filter
   *  never hides a residual without saying so. */
  resolvingHidden: number;
  /** The triage index, straight from `useTriageIndex`. */
  triage: UseTriageIndexResult;
}) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={1.5}
    flexWrap="wrap"
    sx={{ mb: 1 }}
  >
    <Tooltip title="Row count and total are scoped to the currently visible (filtered) rows — clear the filters to see the whole snapshot.">
      <Typography sx={{ fontSize: '0.75rem', color: 'grey.400' }}>
        {totals.rowCount} row{totals.rowCount === 1 ? '' : 's'} shown
      </Typography>
    </Tooltip>
    <Tooltip title="Sum of venue.descalce over non-ambiguous, fully-read, currently-visible (filtered) rows only — an ambiguous claimant carries the full market size, so including it would double-count, and a row with an unread leg has no number to add.">
      <Typography
        data-total-descalce={totals.totalDescalce}
        sx={{ fontSize: '0.75rem', color: 'grey.400' }}
      >
        Total descalce (shown, excl. ambiguous &amp; unread):{' '}
        <Box component="span" sx={{ fontWeight: 700, color: 'common.white' }}>
          {Number.isInteger(totals.totalDescalce)
            ? totals.totalDescalce
            : totals.totalDescalce.toFixed(2)}
        </Box>
      </Typography>
    </Tooltip>
    {totals.triageCount > 0 && (
      <Tooltip title="Visible rows carrying an unread triage card — a stuck counter on the rule, or an orphaned automation fill on the pair. Not gated on the row being descalzada now: a card still waiting for a human is open whatever the current NET reads.">
        <Chip
          size="small"
          label={`${totals.triageCount} with open counter/orphan`}
          color="warning"
          variant="outlined"
          data-triage-rows={totals.triageCount}
          sx={{ fontSize: '0.65rem', height: 20 }}
        />
      </Tooltip>
    )}
    <TriageIndexNotice
      index={triage.data}
      isStale={triage.isStale}
      lastSuccessAt={triage.lastSuccessAt}
    />
    {totals.ambiguousCount > 0 && (
      <Chip
        size="small"
        label={`${totals.ambiguousCount} ambiguous`}
        variant="outlined"
        sx={{ fontSize: '0.65rem', height: 20 }}
      />
    )}
    {totals.unreadCount > 0 && (
      <Tooltip title="Rows whose position could not be read on one of their venue-accounts. They are LEFT OUT of the total above — an unknown is not a zero — and every number on them renders as “—”.">
        <Chip
          size="small"
          label={`${totals.unreadCount} unread`}
          variant="outlined"
          color="warning"
          data-unread-rows={totals.unreadCount}
          sx={{ fontSize: '0.65rem', height: 20 }}
        />
      </Tooltip>
    )}
    {totals.resolvingCount > 0 && (
      <Chip
        size="small"
        label={`${totals.resolvingCount} resolving`}
        variant="outlined"
        color="warning"
        sx={{ fontSize: '0.65rem', height: 20 }}
      />
    )}
    {resolvingHidden > 0 && (
      <Tooltip title="Rows hidden by the “hide settled” filter — one leg already resolved at its venue, but the row still carries a residual imbalance. Counts EVERY hidden row, ambiguous ones included, so it will not match the “resolving” count above, which excludes them. Turn the filter off to review them.">
        <Chip
          size="small"
          label={`${resolvingHidden} settled hidden`}
          variant="outlined"
          data-resolving-hidden={resolvingHidden}
          sx={{ fontSize: '0.65rem', height: 20 }}
        />
      </Tooltip>
    )}
    {(snapshot?.unresolvedAccounts?.ruleCount ?? 0) > 0 && (
      <Tooltip
        title={`Rules the sweeper REFUSED to render because their venue-account pair could not be resolved${
          snapshot?.unresolvedAccounts?.groupIds?.length
            ? ` — group(s): ${snapshot.unresolvedAccounts.groupIds.join(', ')}`
            : ''
        }. They hold positions nobody is watching here; fix the group's accounts to bring them back.`}
      >
        <Chip
          size="small"
          label={`${snapshot?.unresolvedAccounts?.ruleCount} rule(s) with unresolved accounts`}
          variant="outlined"
          color="error"
          data-unresolved-rules={snapshot?.unresolvedAccounts?.ruleCount}
          sx={{ fontSize: '0.65rem', height: 20 }}
        />
      </Tooltip>
    )}
    <Box sx={{ flex: 1 }} />
    {snapshotAccounts(snapshot).map(account => (
      <AccountChip
        key={venueAccountKey(account.platform, account.accountId)}
        account={account}
      />
    ))}
  </Stack>
);
